import Button from "components/form/Button";
import InputField from "components/form/InputField";
import InputRadio from "components/form/InputRadio";
import CustomModal from "components/ui-component/CustomModal";
import { OtpClockIcon } from "icons/auth/OtpClock";
import { SuccessTick } from "icons/auth/SuccessTick";
import { InfoRedCircleIcon } from "icons/subscription/InfoRedCircleIcon";
import { Fragment, useEffect, useState, ChangeEvent } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch } from "store/hooks";
import {
  addNotification,
  contactVerification,
  emailUpdate,
  phoneUpdate,
  updateUserData,
  useUserData,
} from "store/slices/authSlice";
import { decode } from "utils/HelperFunctions";
import { EditData, ShowOtpModal } from "types/UserProfile";

type OTPError = {
  flag: boolean;
  message: string;
};
interface OTPVerificationModalProps {
  showModal: ShowOtpModal;
  setShowModal: React.Dispatch<React.SetStateAction<ShowOtpModal>>;
  setEditData: (d: Partial<EditData>) => void;
  handleCancel: () => void;
  editData: Partial<EditData> | null;
  accountData: Partial<EditData> | Record<string, any>;
}

const OTPVerificationModal = ({
  showModal,
  setShowModal,
  setEditData,
  handleCancel,
  editData,
  accountData,
}: OTPVerificationModalProps) => {
  const dispatch = useAppDispatch();
  const COUNTDOWN = 60;
  const [option, setOption] = useState<"Via phone call" | "Via SMS">("Via SMS");
  const [otpStep, setOtpStep] = useState<number>(1);
  const [seconds, setSeconds] = useState<number>(COUNTDOWN);
  const [startTimer, setStartTimer] = useState<boolean>(false);
  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);
  const userData = useUserData();
  const { clusterId: cluster_id } = useParams();
  const clusterId = decode(cluster_id || "", true);
  const [otpError, setOtpError] = useState<OTPError>({
    flag: false,
    message: "",
  });
  const [otp, setOtp] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (showModal) {
      setSeconds(COUNTDOWN);
    } else {
      setSeconds(0);
    }
  }, [showModal]);

  useEffect(() => {
    if (startTimer) {
      const timer = setInterval(() => {
        if (seconds > 0) {
          setSeconds(seconds - 1);
        }
      }, 1000);
      return () => clearInterval(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startTimer, seconds]);

  const handleChangeVarificationCode = (e: ChangeEvent<HTMLInputElement>) => {
    let inputCode = e.target.value;
    inputCode = inputCode.replace(/\D/g, "");

    inputCode = inputCode.substring(0, 6);

    if (inputCode.length > 3) {
      inputCode = inputCode.substring(0, 3) + " " + inputCode.substring(3);
    }
    setOtp(inputCode);
  };

  useEffect(() => {
    if (!showModal.flag) {
      setTimeout(() => {
        setOtpStep(1);
      }, 500);
    }
    if (showModal.flag && showModal.value && showModal.value === "email") {
      setStartTimer(true);
    }
  }, [showModal]);

  const handleResendCode = async () => {
    if (!showModal.data) return;
    if (showModal.value && showModal.value === "phone") {
      let payload = {
        isPhone: true,
        otp_via: option === "Via SMS" ? "sms" : "call",
        phone: {
          phone_code: showModal?.data?.country_code,
          phone_number: showModal?.data?.phone,
        },
      };
      await dispatch(contactVerification(payload)).unwrap();
    } else {
      let payload = {
        isEmail: true,
        email: showModal?.data?.email,
      };
      await dispatch(contactVerification(payload)).unwrap();
    }
    setOtp("");
    setSeconds(COUNTDOWN);
    setStartTimer(true);
    setOtpError({
      flag: false,
      message: "",
    });
  };

  // One contact_log entry per verification attempt, tagged with what actually
  // happened. Previously a single "modified" entry was written after every
  // attempt — wrong codes included — so the history showed changes that never
  // occurred. The identifier follows the flow: a phone change carries the
  // phone, an email change carries the email.
  const contactLogPayload = (eventStatus: "modified" | "failed") => {
    const isPhoneFlow = showModal?.value === "phone";
    return {
      initiated_by: "contact",
      event_type: "contact",
      userid: userData?.data?.accountData?.leadData?.user_id,
      email: isPhoneFlow ? "" : showModal?.data?.email,
      ...(isPhoneFlow
        ? {
            phone: `+${showModal?.data?.country_code ?? ""}${
              showModal?.data?.phone ?? ""
            }`,
          }
        : {}),
      event_status: eventStatus,
      activity_type: "contact_log",
      clusterid: clusterId,
      communication_status: "",
      accounts_id: userData?.data?.accountData?.id,
    };
  };

  const handleClick = async () => {
    if (!showModal?.data) return;

    setIsButtonLoading(true);
    let newData = { ...accountData };
    if (showModal.value && showModal.value === "phone") {
      let payload = {};
      switch (otpStep) {
        case 1:
          payload = {
            isPhone: true,
            otp_via: option === "Via SMS" ? "sms" : "call",
            phone: {
              phone_code: showModal?.data?.country_code,
              phone_number: showModal?.data?.phone,
            },
          };
          await dispatch(contactVerification(payload)).unwrap();
          setStartTimer(true);
          setIsButtonLoading(false);
          setOtpStep((state) => state + 1);
          break;
        case 2:
          payload = {
            verification_code: otp?.replaceAll(" ", ""),
            phone_type: showModal?.data?.phone_type,
            phone: {
              phone_code: showModal?.data?.country_code,
              phone_number: showModal?.data?.phone,
            },
          };
          await dispatch(phoneUpdate(payload))
            .unwrap()
            .then(async (res: any) => {
              if (res.status === 200) {
                const { data, success } = res.body;
                setIsButtonLoading(false);
                if (success) {
                  setStartTimer(false);
                  setEditData(showModal?.data || {});
                  setOption("Via SMS");
                  handleCancel();
                  setOtpStep((state) => state + 1);
                  newData = { ...accountData, ...showModal?.data };
                  await dispatch(updateUserData(newData));
                  await dispatch(addNotification(contactLogPayload("modified")));
                } else {
                  setSeconds(COUNTDOWN);
                  setOtpError({ flag: true, message: data.msg });
                  await dispatch(addNotification(contactLogPayload("failed")));
                }
              }
            });
          setStartTimer(false);
          break;
        default:
      }
    }
    if (showModal.value && showModal.value === "email") {
      let payload = {
        verification_code: otp?.replaceAll(" ", ""),
        email: showModal?.data?.email,
      };
      await dispatch(emailUpdate(payload))
        .unwrap()
        .then(async (res: any) => {
          if (res.status === 200) {
            const { data, success } = res.body;
            setIsButtonLoading(false);
            if (success) {
              setStartTimer(false);
              setEditData(showModal.data || {});
              handleCancel();
              setOtpStep((state) => state + 1);
              newData = { ...accountData, ...showModal.data };
              await dispatch(updateUserData(newData));
              await dispatch(addNotification(contactLogPayload("modified")));
            } else {
              setSeconds(COUNTDOWN);
              setOtpError({ flag: true, message: data.msg });
              await dispatch(addNotification(contactLogPayload("failed")));
            }
          }
        });
    }
  };

  if (showModal.value && showModal.value === "phone") {
    return (
      <CustomModal
        signModal={false}
        isLoading={false}
        otpModal={true}
        isOpen={showModal.flag}
        className={`w-[462px] ${
          otpStep === 1
            ? "h-[380px]"
            : otpStep === 2
            ? "h-[518px]"
            : otpStep === 3
            ? "h-[223px]"
            : "h-0 hidden"
        }`}
        onDoneClick={() => {
          //   handleWalletDefault(wallet?.id);
        }}
        onCloseClick={() => {
          setShowModal((state) => ({
            ...state,
            flag: false,
            value: null,
          }));
          setOtpStep(-1);
          setOtp(undefined);
          setOtpError({ flag: false, message: "" });
          setEditData(editData || {});
          setIsButtonLoading(false);
          setOption("Via SMS");
          // handleCancel();
        }}
        children={
          <Fragment>
            <div className="w-full h-full flex flex-col justify-between items-center lg:p-0 px-5 py-[30px]">
              {otpStep === 1 || otpStep === 2 ? (
                <OtpClockIcon />
              ) : (
                <SuccessTick />
              )}
              <div className="text-center">
                <div className="text-lg leading-[23.44px] font-bold text-txtPrimary mb-[14px]">
                  {otpStep === 1
                    ? "OTP Delivery Choice"
                    : otpStep === 2
                    ? "OTP Verification"
                    : otpStep === 3
                    ? "Success"
                    : ""}
                </div>
                {/* The narrowing is desktop-only: at 462px it makes the first
                    two steps break where the design does, but on a ~340px
                    phone dialog it forces a third line. The success line needs
                    the full width at any size or it wraps too. */}
                <div
                  className={`text-sm leading-4 font-normal text-txtSecondary text-center whitespace-normal break-words ${
                    otpStep === 3 ? "" : "lg:px-10"
                  }`}
                >
                  {otpStep === 1
                    ? "Select how you wish to receive your one-time password (OTP)."
                    : otpStep === 2
                    ? `Enter the code we sent to ${
                        showModal.data?.phone
                          ? "XXXXX X" + showModal.data.phone.slice(-4)
                          : "XXXXX XXXXX"
                      } `
                    : otpStep === 3
                    ? "Your phone number has been successfully updated."
                    : ""}
                </div>
              </div>
              {otpStep === 1 ? (
                <>
                  <div className="text-center">
                    <InputRadio
                      className="font-medium text-sm"
                      options={[
                        { name: "Phone Call", value: "Via phone call" },
                        { name: "SMS", value: "Via SMS" },
                      ]}
                      value={option}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        console.log(e.target.value);
                        setOption(
                          e.target.value as "Via phone call" | "Via SMS"
                        );
                      }}
                    />
                  </div>
                </>
              ) : otpStep === 2 ? (
                <>
                  <div className="w-full">
                    <InputField
                      type="text"
                      name="otp"
                      label=""
                      className={`!py-[6px] !px-3 text-center ${
                        otpError.flag && "!border-red hover:!border-red"
                      }`}
                      _className="!text-xl !font-medium !leading-normal"
                      placeholder="Enter OTP"
                      value={otp}
                      onChange={(e) => handleChangeVarificationCode(e)}
                    />
                    {otpError.flag ? (
                      <div className="text-xs w-full mt-[12px] bg-lightRedBg text-red px-[10px] py-[12px] flex gap-[5px] rounded-[4px]">
                        <InfoRedCircleIcon />
                        <span>{otpError.message}</span>
                      </div>
                    ) : (
                      <>
                        <div className="mt-[24px] text-center text-blue text-base">
                          {`${Math.floor(seconds / 60)
                            .toString()
                            .padStart(2, "0")}:${(seconds % 60)
                            .toString()
                            .padStart(2, "0")}`}
                        </div>
                      </>
                    )}
                    <div className="mt-4 text-center text-sm ">
                      <div className="text-txtSecondary leading-4">
                        Didn’t receive the code?
                      </div>
                      <div
                        className={`mt-[4px] ${
                          otpError.flag || seconds === 0
                            ? "text-btnPrimaryActive cursor-pointer"
                            : "text-strokeActive pointer-events-none"
                        } pt-[9px] pb-[10px] px-4`}
                      >
                        <span onClick={handleResendCode}>Resend code</span>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <></>
              )}
              {(otpStep === 1 || otpStep === 2) && (
                <Button
                  isLoading={isButtonLoading}
                  className="w-full !py-2.5"
                  text="Continue"
                  onClick={handleClick}
                  isDisabled={
                    isButtonLoading || otpStep === 2
                      ? otp === "" || !otp
                      : false
                  }
                />
              )}
            </div>
          </Fragment>
        }
      />
    );
  } else {
    return (
      <CustomModal
        signModal={false}
        isLoading={false}
        otpModal={true}
        isOpen={showModal.flag}
        className={`w-[462px] ${
          otpStep === 1
            ? "h-[518px]"
            : otpStep === 2
            ? "h-[223px]"
            : "h-0 hidden"
        }`}
        onDoneClick={() => {
          //   handleWalletDefault(wallet?.id);
        }}
        onCloseClick={() => {
          setShowModal((state) => ({
            ...state,
            flag: false,
            value: null,
          }));
          setOtpStep(-1);
          setOtp(undefined);
          setOtpError({ flag: false, message: "" });
          setEditData(editData || {});
          setIsButtonLoading(false);
          setOption("Via SMS");
          // handleCancel();
        }}
        children={
          <Fragment>
            <div className="w-full h-full flex flex-col justify-between items-center  lg:p-0 px-5 py-[30px]">
              {otpStep === 1 ? <OtpClockIcon /> : <SuccessTick />}
              <div className="text-center">
                <div className="text-lg leading-[23.44px] font-bold text-txtPrimary mb-[14px]">
                  {otpStep === 1
                    ? "OTP Verification"
                    : otpStep === 2
                    ? "Success"
                    : ""}
                </div>
                <div className="text-sm leading-4 font-normal text-txtSecondary text-center whitespace-normal break-words">
                  {otpStep === 1
                    ? `Enter the code we sent to ${
                        showModal.data?.email
                          ? showModal.data.email.slice(0, 3) +
                            "****@" +
                            showModal.data.email.split("@")[1]
                          : "XXXXX XXXXX"
                      } `
                    : otpStep === 2
                    ? "Your email has been successfully updated."
                    : ""}
                </div>
              </div>
              {otpStep === 1 ? (
                <>
                  <div className="w-full">
                    <InputField
                      name="text"
                      label=""
                      className={`!py-[6px] !px-3 text-center ${
                        otpError.flag && "!border-red hover:!border-red"
                      }`}
                      _className="!text-xl !font-medium !leading-normal"
                      placeholder="Enter OTP"
                      value={otp}
                      onChange={(e) => {
                        handleChangeVarificationCode(e);
                      }}
                    />
                    {otpError.flag ? (
                      <div className="text-xs w-full mt-[12px] bg-lightRedBg text-red px-[10px] py-[12px] flex gap-[5px] rounded-[4px]">
                        <InfoRedCircleIcon />
                        <span>{otpError.message}</span>
                      </div>
                    ) : (
                      <>
                        <div className="mt-[24px] text-center text-blue text-base">
                          {`${Math.floor(seconds / 60)
                            .toString()
                            .padStart(2, "0")}:${(seconds % 60)
                            .toString()
                            .padStart(2, "0")}`}
                        </div>
                      </>
                    )}
                    <div className="mt-4 text-center text-sm ">
                      <div className="text-txtSecondary leading-4">
                        Didn’t receive the code?
                      </div>
                      <div
                        className={`mt-[4px]  ${
                          otpError.flag || seconds === 0
                            ? "text-btnPrimaryActive cursor-pointer"
                            : "text-strokeActive pointer-events-none"
                        } pt-[9px] pb-[10px] px-4 `}
                      >
                        <span onClick={handleResendCode}>Resend code</span>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <></>
              )}
              {otpStep === 1 && (
                <Button
                  isLoading={isButtonLoading}
                  isDisabled={isButtonLoading || otp === "" || !otp}
                  className="w-full"
                  text="Continue"
                  onClick={handleClick}
                />
              )}
            </div>
          </Fragment>
        }
      />
    );
  }
};

export default OTPVerificationModal;
